import { useState } from 'react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { SearchInput } from '@/components/ui/search-input'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { EmptyState } from '@/components/EmptyState'
import { FileCheck, X } from '@/components/icons'
import { ChecklistCard } from '@/components/ChecklistCard'
import type { TaskStatus } from '@/components/TaskStatusBadge'
import { useTasksToReview } from '@/lib/hooks/task'
import { OperationsTaskReviewDrawer } from '@/components/operations/OperationsTaskReviewDrawer'

interface ReviewTask {
  id: string
  name: string
  status: string
  submittedAt: Date | string | null
  submittedBy: { firstName: string; lastName: string } | null
  aircraft: { registration: string } | null
  tripLeg: {
    departureAirportIcao: string
    arrivalAirportIcao: string
    aircraft: { registration: string } | null
    trip: { tripName: string }
  } | null
}

interface OperationsTasksReviewDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function OperationsTasksReviewDrawer({
  open,
  onOpenChange,
}: OperationsTasksReviewDrawerProps) {
  const { data: tasks, isLoading } = useTasksToReview(open)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const filteredTasks = query
    ? (tasks as ReviewTask[] | undefined)?.filter((t) =>
        [
          t.name,
          t.submittedBy && `${t.submittedBy.firstName} ${t.submittedBy.lastName}`,
          t.tripLeg?.trip.tripName,
          t.tripLeg?.departureAirportIcao,
          t.tripLeg?.arrivalAirportIcao,
          t.aircraft?.registration,
        ]
          .filter(Boolean)
          .some((f) => f!.toLowerCase().includes(query)),
      )
    : (tasks as ReviewTask[] | undefined)

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Review Tasks
            </DrawerTitle>
            <button
              onClick={() => onOpenChange(false)}
              className="size-8 rounded-full bg-surface-L2 flex items-center justify-center hover:bg-surface-L2/80 transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="size-4 text-content-primary-subtle" />
            </button>
          </DrawerHeader>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {isLoading ? (
              <DetailSkeleton rows={5} />
            ) : !tasks?.length ? (
              <EmptyState
                icon={FileCheck}
                title="No tasks to review"
                description="Submitted tasks will appear here for review."
              />
            ) : (
              <div className="flex flex-col gap-3">
                <SearchInput
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClear={() => setSearch('')}
                />
                {filteredTasks?.length ? (
                  <div className="flex flex-col gap-2">
                    {filteredTasks.map((task) => (
                      <ChecklistCard
                        key={task.id}
                        name={task.name}
                        status={task.status as TaskStatus}
                        submittedByName={
                          task.submittedBy
                            ? `${task.submittedBy.firstName} ${task.submittedBy.lastName}`
                            : null
                        }
                        submittedAt={task.submittedAt}
                        tripName={task.tripLeg?.trip.tripName ?? null}
                        departureAirportIcao={task.tripLeg?.departureAirportIcao ?? null}
                        arrivalAirportIcao={task.tripLeg?.arrivalAirportIcao ?? null}
                        aircraftRegistration={
                          task.aircraft?.registration ??
                          task.tripLeg?.aircraft?.registration ??
                          null
                        }
                        onClick={() => setSelectedTaskId(task.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={FileCheck}
                    title="No matching tasks"
                    description="Try a different search."
                  />
                )}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <OperationsTaskReviewDrawer
        taskId={selectedTaskId}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedTaskId(null)
        }}
      />
    </>
  )
}
